import { useEffect } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, useLocation } from "react-router-dom";
import { HelmetProvider } from "react-helmet-async";
import Lenis from "lenis";
import { Toaster } from "sonner";
import { api } from "@/lib/api";
import { AuthProvider } from "@/context/AuthContext";
import { CartProvider } from "@/context/CartContext";
import { CategoriesProvider } from "@/context/CategoriesContext";
import { Header } from "@/components/Header";
import { Footer } from "@/components/Footer";
import { CartDrawer } from "@/components/CartDrawer";
import { InstallPrompt } from "@/components/InstallPrompt";
import HomePage from "@/pages/HomePage";
import ShopPage from "@/pages/ShopPage";
import ProductPage from "@/pages/ProductPage";
import CartPage from "@/pages/CartPage";
import CheckoutPage from "@/pages/CheckoutPage";
import OrderConfirmationPage from "@/pages/OrderConfirmationPage";
import SchoolsPage from "@/pages/SchoolsPage";
import SpecialsPage from "@/pages/SpecialsPage";
import AccountPage from "@/pages/AccountPage";
import AuthPage from "@/pages/AuthPage";
import ResetPasswordPage from "@/pages/ResetPasswordPage";
import AuthCallback from "@/pages/AuthCallback";
import QuotePage from "@/pages/QuotePage";
import StokvelPage from "@/pages/StokvelPage";
import PrivacyPage from "@/pages/PrivacyPage";
import TermsPage from "@/pages/TermsPage";
import AdminPage from "@/pages/admin/AdminPage";
import AdminOverview from "@/pages/admin/AdminOverview";
import AdminProducts from "@/pages/admin/AdminProducts";
import AdminCategories from "@/pages/admin/AdminCategories";
import AdminOrders from "@/pages/admin/AdminOrders";
import AdminOutstanding from "@/pages/admin/AdminOutstanding";
import AdminSales from "@/pages/admin/AdminSales";
import AdminCustomers from "@/pages/admin/AdminCustomers";
import AdminActivity from "@/pages/admin/AdminActivity";
import AdminSchools from "@/pages/admin/AdminSchools";
import AdminPromotions from "@/pages/admin/AdminPromotions";
import AdminQuotations from "@/pages/admin/AdminQuotations";
import AdminSettings from "@/pages/admin/AdminSettings";
import AdminAnalytics from "@/pages/admin/AdminAnalytics";
import AdminStokvel from "@/pages/admin/AdminStokvel";

function ScrollToTop() {
  const { pathname } = useLocation();
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [pathname]);
  return null;
}

// First-party visitor counter: one anonymous session id per tab session (no cookies,
// no personal data), landing referrer + ?utm_source= captured once and reused so a
// visitor who arrived from TikTok/X keeps that source for the whole visit.
// Admin console routes are never tracked.
function AnalyticsTracker() {
  const location = useLocation();
  useEffect(() => {
    if (location.pathname.toLowerCase().startsWith("/admin")) return;
    if (navigator.webdriver) return;
    let sid = sessionStorage.getItem("btm_sid");
    if (!sid) {
      sid = Math.random().toString(36).slice(2) + Date.now().toString(36);
      sessionStorage.setItem("btm_sid", sid);
    }
    const utm = new URLSearchParams(location.search).get("utm_source") || "";
    if (utm) sessionStorage.setItem("btm_utm", utm);
    let ref = sessionStorage.getItem("btm_ref");
    if (ref === null) {
      ref = document.referrer || "";
      sessionStorage.setItem("btm_ref", ref);
    }
    api.post("/track", {
      path: location.pathname,
      referrer: ref,
      utm_source: sessionStorage.getItem("btm_utm") || "",
      sid,
    }).catch(() => {});
  }, [location.pathname, location.search]);
  return null;
}

function useLenis(enabled) {
  useEffect(() => {
    if (!enabled) return;
    const lenis = new Lenis({ lerp: 0.1, smoothWheel: true });
    let raf;
    const loop = (time) => {
      lenis.raf(time);
      raf = requestAnimationFrame(loop);
    };
    raf = requestAnimationFrame(loop);
    return () => {
      cancelAnimationFrame(raf);
      lenis.destroy();
    };
  }, [enabled]);
}

function Shell() {
  const location = useLocation();
  const isAdmin = location.pathname.toLowerCase().startsWith("/admin");
  // Check URL fragment (not query params) for session_id — must run synchronously
  // during render, before any protected-route logic, to avoid race conditions.
  const isAuthCallback = location.hash?.includes("session_id=");
  const isStandalone = window.matchMedia("(display-mode: standalone)").matches || window.navigator.standalone === true;
  // STRICT SEPARATION: website, admin console and installed app each open
  // their own content directly at their own URL. There is NO launch gate —
  // no page ever hijacks another page's URL. (Android sends website links
  // into the installed app when it's installed; behaviour is now identical
  // on every surface, so the website can never "become the app".)
  const isAdminAuth = location.pathname === "/auth" &&
    (new URLSearchParams(location.search).get("next") || "").startsWith("/admin");
  const bareAuth = (isStandalone || isAdminAuth) && location.pathname === "/auth";
  useLenis(!isAdmin && !isAuthCallback);

  if (isAuthCallback) {
    return (
      <>
        <AuthCallback />
        <Toaster position="bottom-right" richColors closeButton />
      </>
    );
  }
  return (
    <>
      <ScrollToTop />
      <AnalyticsTracker />
      {!isAdmin && !bareAuth && <Header />}
      <main className={isAdmin ? "" : "min-h-[60vh]"}>
        <Routes>
          <Route path="/" element={<HomePage />} />
          <Route path="/shop" element={<ShopPage />} />
          <Route path="/shop/:category" element={<ShopPage />} />
          <Route path="/product/:id" element={<ProductPage />} />
          <Route path="/cart" element={<CartPage />} />
          <Route path="/checkout" element={<CheckoutPage />} />
          <Route path="/order/:orderNumber" element={<OrderConfirmationPage />} />
          <Route path="/schools" element={<SchoolsPage />} />
          <Route path="/specials" element={<SpecialsPage />} />
          <Route path="/account" element={<AccountPage />} />
          <Route path="/auth" element={<AuthPage />} />
          <Route path="/reset-password" element={<ResetPasswordPage />} />
          <Route path="/quote" element={<QuotePage />} />
          <Route path="/stokvel" element={<StokvelPage />} />
          <Route path="/privacy" element={<PrivacyPage />} />
          <Route path="/terms" element={<TermsPage />} />
          {/* The admin console answers at BOTH /admin and /admin-login —
              the owner's public link is btmedu.co.za/admin-login */}
          {["/admin", "/admin-login"].map((base) => (
            <Route key={base} path={base} element={<AdminPage />}>
              <Route index element={<AdminOverview />} />
              <Route path="products" element={<AdminProducts />} />
              <Route path="categories" element={<AdminCategories />} />
              <Route path="orders" element={<AdminOrders />} />
              <Route path="outstanding" element={<AdminOutstanding />} />
              <Route path="sales" element={<AdminSales />} />
              <Route path="visitors" element={<AdminAnalytics />} />
              <Route path="stokvel" element={<AdminStokvel />} />
              <Route path="customers" element={<AdminCustomers />} />
              <Route path="activity" element={<AdminActivity />} />
              <Route path="schools" element={<AdminSchools />} />
              <Route path="promotions" element={<AdminPromotions />} />
              <Route path="quotations" element={<AdminQuotations />} />
              <Route path="settings" element={<AdminSettings />} />
            </Route>
          ))}
          <Route path="*" element={<HomePage />} />
        </Routes>
      </main>
      {!isAdmin && !bareAuth && <Footer />}
      {!isAdmin && !bareAuth && <CartDrawer />}
      {!isAdmin && !bareAuth && !isAdminAuth && <InstallPrompt />}
      <Toaster position="bottom-right" richColors closeButton />
    </>
  );
}

function App() {
  return (
    <div className="App">
      <HelmetProvider>
        <BrowserRouter>
          <AuthProvider>
            <CategoriesProvider>
              <CartProvider>
                <Shell />
              </CartProvider>
            </CategoriesProvider>
          </AuthProvider>
        </BrowserRouter>
      </HelmetProvider>
    </div>
  );
}

export default App;
